import type jsPDF from 'jspdf';
import type { Form11ClientParams } from './pdf-types.js';
import { newPDF, placeImage, formatPDFDate, GREEN, type InspectionRow } from './pdf-layout.js';
import { FORM11_ICON_REPORT_NO, FORM10_ICON_LICENSE_NO, FORM10_ICON_REGISTRATION_NO, FORM10_ICON_DATE, FORM10_ICON_SIG_USER, FORM10_ICON_COMPETENT_PERSON, FORM10_ICON_COMPETENCY_CERT, FORM10_ICON_VALIDITY, FORM10_ICON_ISSUING_AUTHORITY, FORM10_ICON_NEXT_INSPECTION } from './form10Assets.js';

// Measured from the user's A4 Canva reference DAHT8XHUQoI, in millimetres.
const LEFT = 7.74, RIGHT = 202.74, NUMBER_W = 6.07, LABEL_X = 16.04, VALUE_X = 106;
const DIVIDER_X = 103.87, TABLE_TOP = 49.56, PAGE_BOTTOM = 288.51;
const ORIGINAL_ROW_HEIGHTS = [5.86, 6, 6, 6, 6, 17.38, 17.38, 6.14, 20.21, 8.91, 23.02, 9.29, 14.07, 5.99, 9.29, 6];
type Weight = 'normal' | 'semibold' | 'bold';
interface Line { text: string; size: number; weight: Weight; height: number }
interface Pair { label?: Line; value?: Line; height: number }

function wrap(doc: jsPDF, text: string, width: number, size: number, weight: Weight, height: number): Line[] {
  doc.setFont('Inter', weight); doc.setFontSize(size);
  return (doc.splitTextToSize((text || '-').replace(/[\u2010-\u2015]/g, '-'), width) as string[])
    .map(text => ({ text, size, weight, height }));
}
function paint(doc: jsPDF, line: Line, x: number, y: number) {
  doc.setFont('Inter', line.weight); doc.setFontSize(line.size);
  doc.text(line.text, x, y, { baseline: 'top' });
}
function text(doc: jsPDF, value: string, x: number, y: number, size: number, weight: Weight = 'normal') {
  paint(doc, { text: value, size, weight, height: 0 }, x, y);
}

/** Canva's design as native PDF text and paths, with content-aware row heights. */
export function renderForm11(data: Form11ClientParams, rows: InspectionRow[], statement: string): jsPDF {
  const doc = newPDF(`Form 11 - ${data.certificate_number}`);
  const person = data.personnel;
  const header = () => {
    doc.setTextColor(...GREEN);
    text(doc, 'Form No. 11', LEFT, 8.3, 28, 'bold');
    text(doc, '(Prescribed Under Rule 61)', LEFT, 20, 11);
    doc.setFillColor(...GREEN); doc.rect(LEFT, 27.66, 11.25, 0.79, 'F');
    doc.setTextColor(96, 160, 28);
    text(doc, 'Report Of Examination Or Test', LEFT, 32.3, 10.5, 'semibold');
    text(doc, 'Of Pressure Vessel Or Plant', LEFT, 37.3, 10.5, 'semibold');

    doc.setDrawColor(...GREEN); doc.setLineWidth(0.2); doc.line(82.18, 11.42, 82.18, 41.88);
    let y = 10.32;
    const metadata = [
      ['Report Number', data.certificate_number, FORM11_ICON_REPORT_NO],
      ['License Number', data.company?.license_number, FORM10_ICON_LICENSE_NO],
      ['Registration Number', data.company?.registration_number, FORM10_ICON_REGISTRATION_NO],
      ['Date', formatPDFDate(data.examination_date), FORM10_ICON_DATE],
    ];
    metadata.forEach(([label, value, icon], i) => {
      const valueLines = wrap(doc, value || '-', RIGHT - 144 - 1, 8, i === 0 ? 'semibold' : 'normal', 3.4);
      const h = Math.max(8.54, valueLines.length * 3.4 + 2.5);
      placeImage(doc, icon, 100.08, y, 6.2, 6.2);
      doc.setTextColor(...GREEN);
      text(doc, label || '', 108.45, y + 1.27, 8.3, 'semibold');
      text(doc, ':', 139.8, y + 1.27, 8.3, 'semibold');
      doc.setTextColor(0, 0, 0);
      valueLines.forEach((line, j) => paint(doc, line, 144, y + 1.27 + j * 3.4));
      if (i < 3) { doc.setDrawColor(...GREEN); doc.setLineWidth(0.15); doc.line(100.08, y + h - 1.3, RIGHT, y + h - 1.3); }
      y += h;
    });
    return Math.max(TABLE_TOP, y + 4.5);
  };
  let y = header();
  const measured = rows.map(row => {
    let labels = row.label.split(/\n(?=\([A-F]\))/);
    let values = row.value.split(/\n(?=\([A-F]\))/);
    // The original template presents vessel and piping on a shared line.
    if (row.number === '10') {
      labels = [labels[0], '(A) Vessel                           (B) Piping'];
      values = [values.join('     ')];
    }
    const pairs: Pair[] = [];
    const paired = labels.length > 1 && /^\(A\)/.test(values[0]);
    let vi = 0;
    for (const label of labels) {
      const part = /^\([A-F]\)/.test(label);
      const labelLines = wrap(doc, label, DIVIDER_X - LABEL_X - 1, part ? 6 : 7, part ? 'normal' : 'semibold', part ? 2.82 : 3.29);
      const value = paired ? part ? values[vi++] || '-' : '' : row.value;
      const valueLines = value ? wrap(doc, value, RIGHT - VALUE_X - 2, 7, 'normal', 3.05) : [];
      for (let i = 0; i < Math.max(labelLines.length, valueLines.length); i++) {
        pairs.push({ label: labelLines[i], value: valueLines[i], height: Math.max(labelLines[i]?.height || 0, valueLines[i]?.height || 0) });
      }
      if (!paired) break;
    }
    return { ...row, pairs, contentH: pairs.reduce((sum, pair) => sum + pair.height, 0) };
  });
  const cert = wrap(doc, statement, RIGHT - LEFT, 7, 'normal', 3.1);
  const detailIcons = [FORM10_ICON_COMPETENT_PERSON, FORM10_ICON_COMPETENCY_CERT, FORM10_ICON_VALIDITY, FORM10_ICON_ISSUING_AUTHORITY, FORM10_ICON_NEXT_INSPECTION];
  const details = [
    ['Name of Competent Person', person?.full_name],
    ['Competency Certificate No.', person?.competency_cert_number],
    ['Validity', [formatPDFDate(person?.competency_valid_from), formatPDFDate(person?.competency_valid_upto)].filter(Boolean).join(' to ')],
    ['Certificate issued by', person?.issuing_authority],
    ['Next Inspection Date', formatPDFDate(data.next_inspection_date)],
  ].map(([label, value]) => ({ label: label || '', lines: wrap(doc, value || '-', 77, 8, 'normal', 3.45) }));
  const detailHeights = details.map(row => Math.max(9.81, row.lines.length * 3.45 + 3));
  const cardH = Math.max(49.18, detailHeights.reduce((sum, h) => sum + h, 0));
  const closingH = 4 + cert.length * 3.1 + 4 + cardH;
  const available = PAGE_BOTTOM - y - closingH;
  let padding = 0.8;
  const minimumTotal = () => measured.reduce((sum, row) => sum + row.contentH + padding * 2, 0);
  if (minimumTotal() > available) padding = 0.5;
  const compactTotal = minimumTotal();
  const desired = measured.map((row, i) => Math.max(ORIGINAL_ROW_HEIGHTS[i], row.contentH + padding * 2));
  const desiredTotal = desired.reduce((sum, h) => sum + h, 0);
  const fitsOnePage = compactTotal <= available;
  const extraRatio = desiredTotal <= available ? 1 : fitsOnePage ? (available - compactTotal) / (desiredTotal - compactTotal) : 1;
  const nextPage = () => { doc.addPage(); y = header(); };
  const drawPart = (row: typeof measured[number], pairs: Pair[], h: number) => {
    doc.setFillColor(...GREEN); doc.rect(LEFT, y, NUMBER_W, h, 'F');
    doc.setDrawColor(0, 0, 0); doc.setLineWidth(0.18);
    doc.rect(LEFT + NUMBER_W, y, RIGHT - LEFT - NUMBER_W, h);
    doc.line(DIVIDER_X, y, DIVIDER_X, y + h);
    doc.setDrawColor(255, 255, 255); doc.line(LEFT, y + h, LEFT + NUMBER_W, y + h);
    doc.setTextColor(255, 255, 255); doc.setFont('Inter', 'bold'); doc.setFontSize(7);
    doc.text(row.number, LEFT + NUMBER_W / 2, y + padding + 1.65, { align: 'center', baseline: 'middle' });
    doc.setTextColor(0, 0, 0);
    let lineY = y + padding;
    for (const pair of pairs) {
      if (pair.label) paint(doc, pair.label, LABEL_X, lineY);
      if (pair.value) paint(doc, pair.value, VALUE_X, lineY);
      lineY += pair.height;
    }
    y += h;
  };
  measured.forEach((row, index) => {
    const natural = row.contentH + padding * 2;
    const height = natural + (desired[index] - natural) * extraRatio;
    if (fitsOnePage || y + height <= 282) { drawPart(row, row.pairs, height); return; }
    if (height <= 45) { nextPage(); drawPart(row, row.pairs, height); return; }
    let offset = 0;
    while (offset < row.pairs.length) {
      let count = 0, h = padding * 2;
      while (offset + count < row.pairs.length && y + h + row.pairs[offset + count].height <= 282) {
        h += row.pairs[offset + count].height; count++;
      }
      if (!count) { nextPage(); continue; }
      drawPart(row, row.pairs.slice(offset, offset + count), h);
      offset += count;
      if (offset < row.pairs.length) nextPage();
    }
  });
  if (y + closingH > PAGE_BOTTOM + 0.01) nextPage();
  const singlePage = doc.getNumberOfPages() === 1;
  const cardY = singlePage ? Math.max(y + 8 + cert.length * 3.1, PAGE_BOTTOM - cardH) : y + 8 + cert.length * 3.1;
  const certY = singlePage ? Math.max(y + 4, Math.min(222.5, cardY - 4 - cert.length * 3.1)) : y + 4;
  doc.setTextColor(0, 0, 0);
  cert.forEach((line, i) => paint(doc, line, LEFT, certY + i * 3.1));
  doc.setFillColor(244, 248, 239); doc.setDrawColor(...GREEN); doc.setLineWidth(0.25);
  doc.roundedRect(9.74, cardY, 49.84, cardH, 3, 3, 'FD');
  doc.roundedRect(61.99, cardY, 138.72, cardH, 3, 3, 'FD');
  placeImage(doc, FORM10_ICON_SIG_USER, 12.96, cardY + 3.17, 7.62, 7.62);
  text(doc, 'Signature of', 22.45, cardY + 3.28, 8, 'semibold');
  text(doc, 'Competent Person', 22.45, cardY + 7, 8, 'semibold');
  placeImage(doc, data.personnel_signature_data_url, 13, cardY + 15, 42, 13);
  placeImage(doc, data.personnel_stamp_data_url, 25, cardY + 29, 20, 18);
  let detailY = cardY;
  details.forEach((detail, i) => {
    placeImage(doc, detailIcons[i], 64.98, detailY + 1.64, 6.26, 6.26);
    text(doc, detail.label, 72.78, detailY + 2.92, 8.1, 'semibold');
    text(doc, ':', 116.15, detailY + 2.92, 8.1, 'semibold');
    detail.lines.forEach((line, j) => paint(doc, line, 120, detailY + 2.92 + j * 3.45));
    detailY += detailHeights[i];
    if (i < 4) { doc.setDrawColor(...GREEN); doc.setLineWidth(0.15); doc.line(64.98, detailY, 197.43, detailY); }
  });
  for (let page = 1; page <= doc.getNumberOfPages(); page++) {
    doc.setPage(page);
    // Inset the Canva footer band so an ordinary A4 printer can reproduce it.
    doc.setFillColor(96, 160, 28); doc.rect(LEFT, 290, RIGHT - LEFT, 2, 'F');
    doc.setFillColor(...GREEN); doc.rect(LEFT, 290, 136, 2, 'F');
    doc.triangle(LEFT + 136, 290, LEFT + 140, 290, LEFT + 138, 292, 'F');
    if (doc.getNumberOfPages() > 1) {
      doc.setTextColor(...GREEN); text(doc, `Page ${page} / ${doc.getNumberOfPages()}${page > 1 ? ' - continued' : ''}`, LEFT, 44, 6);
    }
  }
  return doc;
}
